import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArchiveIcon, CircleDotIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { PRODUCT_STATUSES, useProducts } from '@/api/products'
import type { Product, ProductStatus } from '@/api/products'
import { useWorkspace } from '@/api/workspace'
import { FilterBar } from '@/components/filter-bar'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { ProductBadges } from '@/components/products/product-badges'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

// Products list (UI-SCREENS P-01, #99): code, name, status and archived badges, search, a status
// filter, the Archived toggle and the Variant count (not archived, #102). New Product is Owner only.
// Status and Archived sit in the shared filter bar (as on I-03) and live in the URL; the search box
// stays beside it.
const searchSchema = z.object({
  status: z.enum(PRODUCT_STATUSES).optional().catch(undefined),
  archived: z.boolean().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/products/')({
  component: ProductsList,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'products.title' },
})

function ProductsList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const workspace = useWorkspace(slug)
  const { status, archived = false } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [search, setSearch] = useState('')
  const products = useProducts(slug, { archived })

  const shown = useMemo(() => filterProducts(products.data, search, status), [products.data, search, status])

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('products.title')}</h1>
        {workspace.data?.role === 'owner' ? (
          <Button asChild className="h-11">
            <Link to="/w/$slug/products/new" params={{ slug }}>
              {t('products.newProduct')}
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          type="search"
          className="h-9 sm:max-w-xs"
          aria-label={t('products.search')}
          placeholder={t('products.search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <FilterBar
          fields={[
            {
              key: 'status',
              label: t('products.statusFilter'),
              icon: CircleDotIcon,
              value: status,
              options: PRODUCT_STATUSES.map((value) => ({ value, label: t(`products.status.${value}`) })),
              onChange: (value) =>
                navigate({ search: (prev) => ({ ...prev, status: value as ProductStatus | undefined }), replace: true }),
            },
            {
              key: 'archived',
              label: t('filterBar.archived'),
              icon: ArchiveIcon,
              value: archived ? 'true' : undefined,
              options: [{ value: 'true', label: t('filterBar.shown') }],
              onChange: (value) =>
                navigate({ search: (prev) => ({ ...prev, archived: value === 'true' ? true : undefined }), replace: true }),
            },
          ]}
          onClearAll={() => navigate({ search: {}, replace: true })}
        />
      </div>

      {products.isPending ? (
        <LoadingSkeleton />
      ) : products.isError || !products.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : shown.length === 0 ? (
        <RecordListEmpty>{t('products.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {shown.map((product) => (
            <ProductRow key={product.id} slug={slug} product={product} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function filterProducts(products: Product[] | undefined, search: string, status: ProductStatus | undefined): Product[] {
  const needle = search.trim().toLocaleLowerCase('id-ID')
  return (products ?? [])
    .filter((product) => status === undefined || product.status === status)
    .filter(
      (product) =>
        needle === '' ||
        product.code.toLocaleLowerCase('id-ID').includes(needle) ||
        product.name.toLocaleLowerCase('id-ID').includes(needle),
    )
}

function ProductRow({ slug, product }: { slug: string; product: Product }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      title={
        <RecordLink to="/w/$slug/products/$productId" params={{ slug, productId: String(product.id) }}>
          {product.code} · {product.name}
        </RecordLink>
      }
      badges={<ProductBadges product={product} />}
      figure={t('products.variantCount', { count: product.variant_count })}
    />
  )
}
